import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, ExternalLink, Bell, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import logger from "../lib/logger";
import { trackEventBeacon } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImage = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BACKEND_URL}${path}`;
};

const fmt = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

export default function Shop() {
  const [items, setItems] = useState([]);
  const [footerMessage, setFooterMessage] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [shopItems, content] = await Promise.all([
          api.getShopItems().catch(() => []),
          api.getAllContent().catch(() => ({})),
        ]);
        setItems(shopItems || []);
        setFooterMessage(
          content.shop_footer_message ||
            "Check back often — more official Demolition merchandise is being added all the time."
        );
      } catch (err) {
        logger.warn("Shop data fetch failed:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="bg-demolition-black min-h-screen" data-testid="shop-page">
      {/* Header */}
      <section className="relative py-24 px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0 steel-mesh opacity-20" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            <ShoppingBag className="w-16 h-16 text-demolition-red mx-auto mb-6" />
            <h1
              className="font-russo text-5xl sm:text-6xl lg:text-7xl text-white uppercase tracking-tighter mb-4"
              data-testid="shop-title"
            >
              SHOP
            </h1>
            <p className="font-teko text-xl sm:text-2xl text-demolition-chrome uppercase tracking-widest">
              Official Demolition Merchandise
            </p>
          </div>
        </div>
      </section>

      {/* Items */}
      <section className="py-16 px-6 md:px-12 bg-demolition-paper" data-testid="shop-items-section">
        <div className="max-w-5xl mx-auto">
          {loading && (
            <div className="text-center py-12">
              <p className="font-mono text-white/50 animate-pulse">Loading merchandise...</p>
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="bg-demolition-subtle border border-white/10 p-12 text-center" data-testid="shop-empty-state">
              <ShoppingBag className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="font-mono text-white/60">
                Merchandise listings are on the way. Check back soon!
              </p>
            </div>
          )}

          {!loading && items.length > 0 && (
            <div className="space-y-4" data-testid="shop-items-list">
              {items.map((item) => {
                if (item.price != null && Number(item.price) > 0) {
                  const activeVariations = (item.variations || []).filter((v) => v.active !== false);
                  if (activeVariations.length === 0) {
                    return <UnavailableRow key={item.id} item={item} />;
                  }
                  return <DirectSaleRow key={item.id} item={item} activeVariations={activeVariations} />;
                }
                return <AffiliateRow key={item.id} item={item} />;
              })}
            </div>
          )}
        </div>
      </section>

      {/* Footer message */}
      <section className="py-20 px-6 md:px-12" data-testid="shop-footer-message-section">
        <div className="max-w-3xl mx-auto">
          <div className="bg-demolition-paper border border-demolition-red/30 p-8 md:p-10 text-center">
            <Bell className="w-10 h-10 text-demolition-red mx-auto mb-4" />
            <p
              className="font-teko text-xl md:text-2xl text-white uppercase tracking-widest leading-relaxed"
              data-testid="shop-footer-message"
            >
              {footerMessage}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function ItemThumb({ item }) {
  const src = (item.images && item.images[0]) || item.thumbnail_url;
  if (src) {
    return (
      <img
        src={resolveImage(src)}
        alt={item.name}
        className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0 object-cover border border-demolition-red/30"
        data-testid={`shop-thumb-${item.id}`}
      />
    );
  }
  return (
    <div className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0 bg-demolition-red/10 border border-demolition-red/40 flex items-center justify-center">
      <ShoppingBag className="w-8 h-8 text-demolition-red" />
    </div>
  );
}

function AffiliateRow({ item }) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackEventBeacon("shop_click", item.id, { name: item.name, url: item.url })}
      className="block group bg-demolition-subtle border border-white/10 hover:border-demolition-red/60 transition-colors"
      data-testid={`shop-item-${item.id}`}
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4 p-6 md:p-8">
        <ItemThumb item={item} />
        <div className="flex-1 min-w-0">
          <h2 className="font-russo text-2xl md:text-3xl text-white uppercase tracking-tighter group-hover:text-demolition-red transition-colors break-words">
            {item.name}
          </h2>
          {item.description && (
            <p className="font-mono text-white/60 text-sm mt-2">{item.description}</p>
          )}
          <p className="font-mono text-white/30 text-xs mt-2 truncate">{item.url}</p>
        </div>
        <div className="flex items-center gap-2 text-demolition-chrome group-hover:text-demolition-red transition-colors flex-shrink-0">
          <span className="font-teko text-lg uppercase tracking-widest">Visit</span>
          <ExternalLink className="w-5 h-5" />
        </div>
      </div>
    </a>
  );
}

/**
 * Direct-sale item that has NO active variations yet (staged / coming soon).
 * Rendered non-interactive so bots can index the name and fans see a friendly message.
 */
function UnavailableRow({ item }) {
  return (
    <div
      className="block bg-demolition-subtle border border-white/10 opacity-70"
      data-testid={`shop-item-${item.id}`}
      aria-disabled="true"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4 p-6 md:p-8">
        <ItemThumb item={item} />
        <div className="flex-1 min-w-0">
          <h2 className="font-russo text-2xl md:text-3xl text-white/70 uppercase tracking-tighter break-words">
            {item.name}
          </h2>
          {item.description && (
            <p className="font-mono text-white/50 text-sm mt-2 line-clamp-2">{item.description}</p>
          )}
          <p
            className="font-teko text-sm text-yellow-300/90 uppercase tracking-widest mt-3"
            data-testid={`shop-unavailable-${item.id}`}
          >
            Not currently available
          </p>
        </div>
      </div>
    </div>
  );
}

function DirectSaleRow({ item, activeVariations }) {
  const variations = activeVariations;
  const totalStock = variations.reduce((s, v) => s + Math.max(0, Number(v.inventory || 0)), 0);
  const isSoldOut = totalStock === 0;
  const cheapest = Math.min(
    ...variations.map((v) =>
      v.price != null ? Number(v.price) : Number(item.price)
    )
  );
  const priceLabel = variations.length > 1 ? `From ${fmt(cheapest)}` : fmt(cheapest);

  return (
    <Link
      to={`/shop/p/${item.slug || item.id}`}
      onClick={() => trackEventBeacon("shop_click", item.id, { name: item.name })}
      className="block group bg-demolition-subtle border border-white/10 hover:border-demolition-red/60 transition-colors"
      data-testid={`shop-item-${item.id}`}
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4 p-6 md:p-8">
        <ItemThumb item={item} />
        <div className="flex-1 min-w-0">
          <h2 className="font-russo text-2xl md:text-3xl text-white uppercase tracking-tighter group-hover:text-demolition-red transition-colors break-words">
            {item.name}
          </h2>
          {item.description && (
            <p className="font-mono text-white/60 text-sm mt-2 line-clamp-2">{item.description}</p>
          )}
          <p
            className="font-russo text-demolition-red text-2xl md:text-3xl mt-3"
            data-testid={`shop-price-${item.id}`}
          >
            {priceLabel}
          </p>
          {isSoldOut && (
            <p className="font-teko text-sm text-white/50 uppercase tracking-widest mt-1">Sold Out</p>
          )}
        </div>
        <div className="flex items-center gap-2 text-demolition-chrome group-hover:text-demolition-red transition-colors flex-shrink-0">
          <span className="font-teko text-lg uppercase tracking-widest">View</span>
          <ChevronRight className="w-5 h-5" />
        </div>
      </div>
    </Link>
  );
}
